<script setup>
  /**
   * TransformLearningDashboard - Internal BCS Transform™ Learning Dashboard view.
   *
   * Rendered through the custom virtual router (NavigationStore route
   * `transformLearningDashboard`) from HomePage's component switch. Access is gated
   * on the `transform.access` RBAC permission: users without it are redirected back
   * to Help & Resources, and the view only renders once the access check resolves to
   * avoid a flash of content.
   *
   * The header shows a fixed title plus dynamic copy resolved from the RTDB `BCS`
   * tree via BcsDynamicText (the subtitle, and a clickable certificate term that
   * opens BcsCertificationPanel). The body holds the progress card, continue-lesson
   * card, module grid, and bonus learning sections.
   *
   * @prop - none (uses inject() for `customNavigation` and `toggleLoadingWithMessage`)
   * @emits - none
   */
  import Footer from '../FooterSection.vue'
  import Breadcrumbs from '../BreadCrumbs.vue'
  import TransformOwnerBanner from './TransformOwnerBanner.vue'
  import TransformModuleGrid from './TransformModuleGrid.vue'
  import TransformProgressCard from './TransformProgressCard.vue'
  import TransformContinueCard from './TransformContinueCard.vue'
  import TransformErrorState from './TransformErrorState.vue'
  import BcsCertificationPanel from './BcsCertificationPanel.vue'
  import BcstCourseProgressPanel from './BcstCourseProgressPanel.vue'
  import BcsSearchDropdown from '../BcsSearchDropdown.vue'
  import BcsDynamicText from '../../components/BcsDynamicText.vue'
  import { useTransformLearningStore } from '../../stores/TransformLearningStore'
  import { useTransformSearch } from '../../composables/useTransformSearch'
  import { useLessonThumbnails } from '../../composables/useLessonThumbnails'
  import { useTransformAccessGate } from '../../composables/useTransformAccessGate'
  import { inject, onMounted, ref } from 'vue'

  const customNavigation = inject('customNavigation')
  const toggleLoadingWithMessage = inject('toggleLoadingWithMessage', () => {})
  const store = useTransformLearningStore()
  const { searchItems } = useTransformSearch()
  const { resolveThumbnail } = useLessonThumbnails('TransformLearningDashboard')
  const { isAccessChecked, ensureAccess } = useTransformAccessGate()

  const certPanelRef = ref(null)
  const progressPanelRef = ref(null)
  const continueThumbnailUrl = ref(null)

  // A search hands off to the dedicated results page, carrying the term and marking the
  // dashboard as the origin so its "Clear Search" returns here.
  function goToSearchResults(term) {
    const t = (term ?? '').trim()
    if (!t) return
    customNavigation.value.navigate({
      newPage: customNavigation.value.routes.transformSearchResults.name,
      params: {
        searchTerm: t,
        searchOrigin: customNavigation.value.routes.transformLearningDashboard.name,
      },
    })
  }

  async function loadDashboard() {
    toggleLoadingWithMessage(true, null)
    try {
      // The catalogue is stable, so it is loaded once and reused across visits; only the
      // user's progress is refreshed each time so a just-watched lesson shows as complete
      // without refetching the whole course + modules + lessons.
      await store.loadDashboard()
      await store.refreshProgress()

      const cl = store.continueLesson
      // Quiz meta/lock data and the thumbnail are independent, so resolve them in parallel.
      const [, thumbUrl] = await Promise.all([
        cl?.module?.id ? store.loadCategoryDetail(cl.module.id) : Promise.resolve(),
        resolveThumbnail(cl?.lesson),
      ])
      continueThumbnailUrl.value = thumbUrl
    } finally {
      toggleLoadingWithMessage(false, null)
    }
  }

  function openProgressPanel() {
    progressPanelRef.value?.open()
  }

  onMounted(async () => {
    if (!(await ensureAccess())) return
    await loadDashboard()
    isAccessChecked.value = true
    window.scrollTo({ top: 0, behavior: 'smooth' })
  })
</script>

<template>
  <div v-if="isAccessChecked && store.error" class="transform-learning-dashboard bcs-page-padding mt-4">
    <Breadcrumbs></Breadcrumbs>
    <TransformErrorState :message="store.error" @retry="loadDashboard" />
    <Footer></Footer>
  </div>
  <div v-else-if="isAccessChecked" class="transform-learning-dashboard">
    <TransformOwnerBanner />
    <div class="mt-4 bcs-page-padding">
      <div class="row">
        <div class="col-12">
          <Breadcrumbs></Breadcrumbs>
          <div class="row">
            <div class="col-12 col-xl-6">
              <h1 class="text-28 tld-title">
                <med>BCS Transform™ Learning Dashboard</med>
              </h1>
              <p class="text-14 tld-subtitle">
                <BcsDynamicText text="bcstLearningDashboardSubtitleDefinition" />
              </p>
              <p class="text-16 mb-0">
                <span
                  class="words-for-definition no-close"
                  role="button"
                  tabindex="0"
                  @click="certPanelRef?.open()"
                  @keydown.enter.prevent="certPanelRef?.open()"
                  @keydown.space.prevent="certPanelRef?.open()">
                  <BcsDynamicText text="bcsComplianceCertificateBtn" />
                </span>
              </p>
            </div>
          </div>

          <!-- Section wrappers use data-section markers and id hooks as stable targeting anchors. -->
          <section
            id="transform-your-progress"
            data-section="your-progress"
            class="tld-your-progress">
            <TransformProgressCard @viewProgressDetails="openProgressPanel" />
          </section>
        </div>
      </div>
    </div>

    <!-- Full-width continue section — must live outside bcs-page-padding so the
         background spans edge to edge; internal padding mirrors the page margins. -->
    <section v-if="store.continueLesson" id="transform-continue" data-section="continue" class="tld-continue-section">
      <TransformContinueCard
        :lesson="store.continueLesson.lesson"
        :module="store.continueLesson.module"
        :index="store.continueLesson.lessonIndex"
        :total="store.continueLesson.lessonTotal"
        :thumbnailUrl="continueThumbnailUrl"
        :isFirstInScope="store.completedLessons === 0" />
    </section>

    <div class="bcs-page-padding">
      <div class="row">
        <div class="col-12">
          <section
            id="transform-learning-library"
            data-section="your-learning-library"
            class="mt-5">
            <div class="d-flex align-items-center justify-content-between tld-library-header">
              <h2 class="header-24 mb-0"><strong>Your Learning Library</strong></h2>

              <!-- Search bar: hands off to the dedicated search-results page on search. -->
              <div class="tld-search">
                <BcsSearchDropdown
                  id="transformLearningSearch"
                  placeholder="Search lessons, quizzes, or topics"
                  searchButtonText=""
                  :items="searchItems"
                  :searchFields="['displayTitle', 'description']"
                  :arrayTerm="'displayTitle'"
                  :maxSuggestions="10"
                  :showResults="false"
                  @search="(payload) => goToSearchResults(payload.term)"
                  @select="(item) => goToSearchResults(item.displayTitle)"
                  @clear="() => {}" />
              </div>
            </div>

            <TransformModuleGrid
              :modules="store.modulesWithLessonCount"
              :progressByModule="store.progressByModule" />
          </section>

          <section id="transform-bonus-learning" data-section="bonus-learning">
            <!-- Bonus Learning placeholder -->
          </section>
        </div>
      </div>
    </div>

    <Footer></Footer>

    <BcsCertificationPanel
      ref="certPanelRef"
      @viewProgressDetails="openProgressPanel" />
    <BcstCourseProgressPanel ref="progressPanelRef" />
  </div>
</template>

<style scoped lang="scss">
  .transform-learning-dashboard {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }

  .tld-title {
    letter-spacing: -0.56px;
    margin-top: 32px;
    margin-bottom: 8px;
  }

  /* Scope the subtitle's spacing to a dedicated class instead of overriding the global
     .text-14 typography utility (which would shift every .text-14 in this subtree). */
  .tld-subtitle {
    margin-bottom: 20px;
  }

  .tld-library-header {
    margin-bottom: 41px;
  }

  .tld-search {
    max-width: 560px;
    flex: 1;
    margin-left: 24px;
  }

  .tld-your-progress {
    margin-top: 34px;
    margin-bottom: 0;
  }

  .tld-continue-section {
    margin-top: 40px;
  }
</style>
